import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { importsApi, type ImportVersion } from '@/lib/imports';

interface Props {
  version: ImportVersion;
  onVersion: (v: ImportVersion) => void;
  onNext: () => void;
  onBack: () => void;
}

/** Прогресс извлечения читается с сервера (опрос раз в 2 с): страницы, позиции, ошибки, стоимость. */
export default function StepExtraction({ version, onVersion, onNext, onBack }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const running = !version.finishedAt;
  const totalPages = version.filePages ?? 0;
  const page = version.pagesDone;
  const progress = totalPages ? Math.round((page / totalPages) * 100) : 0;
  const itemsFound = version.pagesLog.reduce((s, p) => s + (p.items || 0), 0);
  const failed = version.pagesLog.filter((p) => p.status === 'failed').length;

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      importsApi
        .get(version.id)
        .then((v) => {
          onVersion(v);
          setError(null);
        })
        .catch((e: ApiError) => setError(e.code));
    }, 2000);
    return () => window.clearInterval(timer);
  }, [running, version.id, onVersion]);

  const retry = async () => {
    setBusy(true);
    try {
      onVersion(await importsApi.retry(version.id));
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  const log = version.pagesLog.slice(0, Math.max(page + 3, 12));

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="flex flex-col gap-4 xl:col-span-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                  <i className="ri-sparkling-2-line text-[14px]" />
                </span>
                <h2 className="text-sm font-semibold text-foreground-950">{running ? t('impExtract.titleRun') : t('impExtract.titleDone')}</h2>
              </div>
              <p className="mt-1 text-[12px] text-foreground-500">{t('impExtract.meta', { file: version.fileName ?? '', pages: totalPages, partner: version.partnerName ?? '' })}</p>
            </div>
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-800">
              <span className={`h-1.5 w-1.5 rounded-full ${running ? 'animate-pulse bg-primary-500' : 'bg-primary-600'}`} />
              {running ? t('impExtract.pageOf', { page, total: totalPages }) : t('impExtract.complete')}
            </span>
          </div>

          <div className="mt-4">
            <div className="flex items-end justify-between text-[11.5px]">
              <p className="tnum font-medium text-foreground-800">
                {running ? t('impExtract.progressLine', { page, total: totalPages, items: itemsFound }) : t('impExtract.extracted', { count: version.itemsCount ?? itemsFound })}
              </p>
              <p className="tnum text-foreground-500">{progress}%</p>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
              <div className="h-full rounded-full bg-primary-500 transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: t('impExtract.statItems'), value: String(version.itemsCount ?? itemsFound) },
              { label: t('impExtract.statTables'), value: String(version.pagesLog.filter((p) => p.status === 'done').length) },
              { label: t('impX.statRisk'), value: version.finishedAt ? `AED ${Math.round(version.totalRiskAed).toLocaleString('en-AE')}` : '—' },
              { label: t('impExtract.statUnreadable'), value: String(failed) },
            ].map((s) => (
              <div key={s.label} className="rounded-md border border-background-200 bg-background-100/60 px-2.5 py-2">
                <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-foreground-400">{s.label}</p>
                <p className="tnum mt-0.5 text-sm font-semibold text-foreground-950">{s.value}</p>
              </div>
            ))}
          </div>

          {(version.error || error) && (
            <p className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
              {version.error ?? `${t('ui.error')}: ${error}`}{' '}
              {!running && (
                <button type="button" disabled={busy} onClick={() => void retry()} className="cursor-pointer font-semibold underline">
                  {t('impX.retryExtraction')}
                </button>
              )}
            </p>
          )}
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-950">{t('impExtract.logTitle')}</h2>
          <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('impExtract.logSub')}</p>
          <ul className="mt-3 divide-y divide-background-200">
            {log.map((row) => {
              const status = row.status === 'done' ? 'done' : row.status === 'failed' ? 'failed' : running && row.page === page + 1 ? 'active' : 'queued';
              return (
                <li key={row.page} className="flex items-center gap-3 py-2">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                      status === 'done' ? 'bg-primary-100 text-primary-700' : status === 'active' ? 'bg-accent-100 text-accent-800' : status === 'failed' ? 'bg-rose-100 text-rose-700' : 'bg-background-100 text-foreground-400'
                    }`}
                  >
                    {status === 'done' ? <i className="ri-check-line text-[11px]" /> : status === 'failed' ? <i className="ri-close-line text-[11px]" /> : row.page}
                  </span>
                  <p className="flex-1 text-[12px] text-foreground-800">{t('impExtract.page', { page: row.page })}</p>
                  <p className="tnum text-[11.5px] text-foreground-500">{status === 'queued' ? t('impExtract.queued') : status === 'failed' ? t('impX.pageFailed') : t('impExtract.items', { count: row.items })}</p>
                  {status === 'active' && <span className="text-[10.5px] font-medium text-accent-700">{t('impExtract.reading')}</span>}
                </li>
              );
            })}
            {log.length === 0 && <li className="py-2 text-[12px] text-foreground-400">{t('ui.loading')}</li>}
          </ul>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h2 className="text-sm font-semibold text-foreground-950">{t('impExtract.sourceTitle')}</h2>
          <div className="mt-2 overflow-hidden rounded-md border border-background-200 bg-background-100 p-3 text-[11px] leading-4 text-foreground-600">
            <p>
              {t('impX.modeLabel')}: <span className="font-medium text-foreground-900">{t(`impX.mode.${version.mode}`)}</span>
            </p>
            <p className="mt-1">
              {t('impX.modelLabel')}: <span className="font-medium text-foreground-900">{version.mode === 'table' ? '—' : 'Claude Sonnet'}</span>
            </p>
            <p className="tnum mt-1">
              {t('impX.costLabel')}: <span className="font-medium text-foreground-900">${version.costUsd.toFixed(2)}</span> · {version.tokensIn ?? 0}/{version.tokensOut ?? 0}
            </p>
          </div>
          <p className="mt-2 text-[11px] leading-4 text-foreground-500">{t('impExtract.sourceNote')}</p>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h2 className="text-sm font-semibold text-foreground-950">{t('impExtract.rulesTitle')}</h2>
          <ul className="mt-2 space-y-1.5 text-[11px] leading-4 text-foreground-600">
            <li className="flex gap-1.5">
              <i className="ri-check-line text-[12px] text-primary-600" /> {t('impExtract.rule1')}
            </li>
            <li className="flex gap-1.5">
              <i className="ri-question-line text-[12px] text-accent-600" /> {t('impExtract.rule2')}
            </li>
            <li className="flex gap-1.5">
              <i className="ri-error-warning-line text-[12px] text-accent-700" /> {t('impExtract.rule3')}
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-4">
          <button
            type="button"
            disabled={running || !version.itemsCount}
            onClick={onNext}
            className="flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-[12.5px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('impExtract.review', { count: version.itemsCount ?? itemsFound })} <i className="ri-arrow-right-line text-[13px]" />
          </button>
          <button type="button" onClick={onBack} className="h-8 w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 text-[11.5px] font-medium text-foreground-700 hover:bg-background-100">
            {t('impExtract.backUpload')}
          </button>
          <p className="text-[10.5px] leading-4 text-foreground-400">{t('impExtract.note')}</p>
        </div>
      </div>
    </div>
  );
}
